import { useEffect, useMemo, useRef, useState } from "react";

import Head from "next/head";
import Image from "next/image";
import { useRouter } from "next/router";

import { GoogleLogin, type CredentialResponse } from "@react-oauth/google";
import { toast } from "sonner";

import schoolsIcon from "@/assets/icons/schools-icon.png";
import teachersIcon from "@/assets/icons/teachers-icon.png";
import RocketLoading from "@/components/atoms/loading";
import { GamifikTopBar } from "@/components/organisms/gamefik-top-bar";
import { Button } from "@/components/ui/button";
import { useClientHostname } from "@/hooks/use-client-hostname";
import { useGeoPricing } from "@/hooks/use-geo-pricing";
import { useIsTestEnvironment } from "@/hooks/use-is-test-environment";
import { resolveOnboardingRedirectTarget } from "@/hooks/use-onboarding-variant";
import { useQueryClient } from "@tanstack/react-query";

import { normalizeLocale } from "@/i18n/detect-locale";
import { clearLocalStoragePreservingLocale, readStoredLocale } from "@/i18n/storage";
import { useI18n } from "@/i18n/use-i18n";
import { Analytics } from "@/lib/analytics";
import { initAppleAuth, signInWithApple, type AppleAuthResponse } from "@/lib/apple-auth";
import { persistPostHogSignupAttribution } from "@/lib/attribution";
import { createMetaTeacherRegistrationTracker } from "@/lib/meta-pixel";
import { identifyFromToken, posthog } from "@/lib/posthog";
import { hasLocalOnboardingCompletion } from "@/lib/onboarding-completion";
import { isTestEnvironmentHost } from "@/lib/test-environment";
import AuthService from "@/services/auth";
import Client from "@/services/client";
import Settings from "@/services/settings";
import Validator from "@/services/validator";

const loginClient = new Client();

// ── Helpers de estilo ─────────────────────────────────────────────────────────

const sora = { fontFamily: "var(--font-turmas-sora, 'Sora', sans-serif)" } as const;
const poppins = { fontFamily: "var(--font-turmas-poppins, 'Poppins', sans-serif)" } as const;
const jakartaSans = { fontFamily: "var(--font-turmas-plus-jakarta, 'Plus Jakarta Sans', sans-serif)" } as const;
const sfPro = { fontFamily: "-apple-system, 'SF Pro Display', 'SF Pro Text', system-ui, sans-serif" } as const;

const inputBase: React.CSSProperties = {
  ...sora,
  width: "100%",
  height: 40,
  borderRadius: 5,
  border: "1.5px solid rgba(66, 80, 102, 0.4)",
  backgroundColor: "#fff",
  color: "var(--foreground)",
  padding: "0 2.75rem 0 1rem",
  fontSize: "0.875rem",
  outline: "none",
  transition: "border-color 0.15s, box-shadow 0.15s",
  boxSizing: "border-box",
};

function handleInputFocus(e: React.FocusEvent<HTMLInputElement>) {
  e.target.style.borderColor = "oklch(0.88 0.17 165)";
  e.target.style.boxShadow = "0 0 0 3px oklch(0.88 0.17 165 / 0.25)";
}
function handleInputBlur(e: React.FocusEvent<HTMLInputElement>) {
  e.target.style.borderColor = "rgba(66, 80, 102, 0.4)";
  e.target.style.boxShadow = "none";
}

// ── Ícones reutilizáveis ──────────────────────────────────────────────────────

function RocketSvg() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M0.275 8.19442L4.475 3.99442C4.70833 3.76109 4.98333 3.59442 5.3 3.49442C5.61667 3.39442 5.94167 3.37775 6.275 3.44442L7.575 3.71942C6.675 4.78609 5.96667 5.75275 5.45 6.61942C4.93333 7.48609 4.43333 8.53609 3.95 9.76942L0.275 8.19442ZM5.4 10.4694C5.78333 9.26942 6.30417 8.13609 6.9625 7.06942C7.62083 6.00275 8.41667 5.00275 9.35 4.06942C10.8167 2.60275 12.4917 1.50692 14.375 0.781922C16.2583 0.0569215 18.0167 -0.163912 19.65 0.119421C19.9333 1.75275 19.7167 3.51109 19 5.39442C18.2833 7.27775 17.1917 8.95275 15.725 10.4194C14.8083 11.3361 13.8083 12.1319 12.725 12.8069C11.6417 13.4819 10.5 14.0111 9.3 14.3944L5.4 10.4694ZM12.3 7.46942C12.6833 7.85275 13.1542 8.04442 13.7125 8.04442C14.2708 8.04442 14.7417 7.85275 15.125 7.46942C15.5083 7.08609 15.7 6.61525 15.7 6.05692C15.7 5.49859 15.5083 5.02775 15.125 4.64442C14.7417 4.26109 14.2708 4.06942 13.7125 4.06942C13.1542 4.06942 12.6833 4.26109 12.3 4.64442C11.9167 5.02775 11.725 5.49859 11.725 6.05692C11.725 6.61525 11.9167 7.08609 12.3 7.46942ZM11.6 19.4944L10 15.8194C11.2333 15.3361 12.2875 14.8361 13.1625 14.3194C14.0375 13.8028 15.0083 13.0944 16.075 12.1944L16.325 13.4944C16.3917 13.8278 16.375 14.1569 16.275 14.4819C16.175 14.8069 16.0083 15.0861 15.775 15.3194L11.6 19.4944ZM1.875 13.6694C2.45833 13.0861 3.16667 12.7903 4 12.7819C4.83333 12.7736 5.54167 13.0611 6.125 13.6444C6.70833 14.2278 7 14.9361 7 15.7694C7 16.6028 6.70833 17.3111 6.125 17.8944C5.70833 18.3111 5.0125 18.6694 4.0375 18.9694C3.0625 19.2694 1.71667 19.5361 0 19.7694C0.233333 18.0528 0.5 16.7111 0.8 15.7444C1.1 14.7778 1.45833 14.0861 1.875 13.6694Z" fill="white" />
    </svg>
  );
}

function EyeOffSvg() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
      <line x1="2" x2="22" y1="2" y2="22" />
    </svg>
  );
}

function EyeOnSvg() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function decodeGoogleJwt(credential: string): { given_name?: string; family_name?: string; email?: string; name?: string } | null {
  try {
    const parts = credential.split(".");
    if (parts.length !== 3) return null;
    const base64 = parts[1]!.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, "=");
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

/**
 * Logins internos no estilo do api-v2 (`gfk-mundo`, `gfk=email`, `gfk+mundo`)
 * entram direto, sem etapa de senha nem código. Para `gfk-`/`gfk=` o backend
 * ignora a senha; para `gfk+` ele espera a senha derivada `${email}${world.length}`
 * (contrato do authGamefikManager), que dá pra calcular aqui mesmo.
 * Retorna a senha a enviar, ou null quando não é um login gfk.
 */
function getGfkDirectPassword(rawEmail: string): string | null {
  const trimmed = rawEmail.trim();
  if (trimmed.includes("gfk+")) {
    const world = trimmed.replace("gfk+", "");
    return `${trimmed}${world.length}`;
  }
  if (trimmed.includes("gfk-") || trimmed.includes("gfk=")) {
    return "";
  }
  return null;
}

/**
 * authenticatedRequest resolve erros como `{...json, error: json}` — a mensagem
 * real pode estar em `error` (string) ou `error.error` (objeto do backend).
 */
function extractApiErrorMessage(err: any): string {
  if (!err) return "";
  if (typeof err.error === "string") return err.error;
  if (typeof err.error?.error === "string") return err.error.error;
  if (typeof err.message === "string") return err.message;
  return "";
}

/**
 * Lê `onboarding_professor_completed` do backend distinguindo "o servidor disse
 * que não concluiu" de "não deu para saber". `authenticatedRequest` (instância)
 * NUNCA rejeita — em erro de rede/HTTP ele RESOLVE `{ error, ... }` — então sem
 * checar `.error` explicitamente toda falha vira um "não concluído" falso.
 */
async function readRemoteOnboardingCompletion(
  client: { authenticatedRequest: (method: "GET", path: string) => Promise<any> },
): Promise<{ completed: boolean; checkFailed: boolean }> {
  try {
    const userInfo = await client.authenticatedRequest("GET", "users/info");
    if (userInfo?.error) {
      console.error("[onboarding] users/info retornou erro:", userInfo.error);
      return { completed: false, checkFailed: true };
    }
    return { completed: Boolean(userInfo?.onboarding_professor_completed), checkFailed: false };
  } catch (err: any) {
    console.error("[onboarding] users/info falhou:", err?.message || err?.error || err);
    return { completed: false, checkFailed: true };
  }
}

function getSchoolSignupContinuation(account?: { signupProfile?: "teacher" | "school"; onboardingCompletedAt?: string | Date | null; ["new-manager-global"]?: boolean }, setupCompleted?: boolean) {
  if (account?.signupProfile === "school") {
    return setupCompleted ? "/chat-ia" : "/signup/school-onboarding";
  }
  if (account?.signupProfile === "teacher") {
    return "/signup/teacher-onboarding";
  }
  return "/signup/plans";
}

// ── Carousel ─────────────────────────────────────────────────────────────────

type Slide = {
  id: number;
  bgImage: string;
  slideKey: "s1" | "s2" | "s3" | "s4" | "s5" | "s6";
  author: string;
  avatar?: string;
};

const SLIDES: Slide[] = [
  { id: 1, bgImage: "/firecamp-bg.png", slideKey: "s1", author: "Rodrigo Tavares" },
  { id: 2, bgImage: "/camp-bg.png", slideKey: "s2", author: "Patrícia Carvalho" },
  { id: 3, bgImage: "/firecamp-bg.png", slideKey: "s3", author: "Juliana Martins" },
  { id: 4, bgImage: "/camp-bg.png", slideKey: "s4", author: "Paula Ferreira" },
  { id: 5, bgImage: "/firecamp-bg.png", slideKey: "s5", author: "Gilterlan Souza" },
  { id: 6, bgImage: "/camp-bg.png", slideKey: "s6", author: "Mariana Ribeiro" },
];

export function LoginCarousel() {
  const { t } = useI18n();
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  function goTo(index: number) {
    if (animating || index === current) return;
    setAnimating(true);
    setTimeout(() => {
      setCurrent(index);
      setAnimating(false);
    }, 300);
  }

  useEffect(() => {
    if (
      typeof window.matchMedia === "function"
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) return;

    const timer = setInterval(() => {
      goTo((current + 1) % SLIDES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [current, animating]);

  const slide = SLIDES[current] ?? SLIDES[0]!;

  return (
    <div
      className="relative w-full md:h-full overflow-hidden bg-white"
      style={{
        backgroundImage: `url(${slide.bgImage})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "100% auto",
        backgroundPosition: "top center",
        transition: "background-image 0.3s ease",
      }}
    >
      {/* Âncora de altura no mobile: img natural define a altura do container */}
      <img
        src={slide.bgImage}
        alt=""
        aria-hidden
        className="w-full block md:hidden"
        style={{ visibility: "hidden" }}
      />
      <div className="absolute inset-0 z-10 flex flex-col items-center justify-start px-6 md:relative md:h-full">
        {/* card */}
        <div
          className="gds-card relative w-full flex flex-col gap-3"
          style={{
            padding: "clamp(16px, 2vw, 28px)",
            maxWidth: "clamp(300px, 85%, 460px)",
            marginTop: "clamp(16px, 8vw, 180px)",
            opacity: animating ? 0 : 1,
            transform: animating ? "translateY(8px) rotate(-2deg)" : "translateY(0) rotate(-2deg)",
            transition: "opacity 0.3s ease, transform 0.3s ease",
            boxShadow: "0 0 25px 0 color-mix(in srgb, var(--primary) 25%, transparent)",
          }}
        >
          {/* badge */}
          <div className="absolute -top-2 -right-4" style={{ transform: "rotate(4deg)" }}>
            <span
              className="flex items-center gap-1 uppercase tracking-widest"
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "var(--primary)",
                backgroundColor: "#55FCB9",
                borderRadius: 999,
                padding: "12px 18px 12px 14px",
                letterSpacing: "-0.64px",
                gap: 8,
                boxShadow: "0 0 26px 5.5px rgba(0,0,0,0.10), 0 8.6px 10.75px -6.45px rgba(0,0,0,0.10)",
              }}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3.26229 9.16217L5.71405 7.5501L7.91759 9.4877L7.24615 6.54619L9.68977 5.05051L6.83839 4.85112L6.11893 1.76007L4.97627 4.72091L2.1249 4.52152L4.33658 6.34274L3.26229 9.16217ZM5.42718 11.6526C4.6222 11.5963 3.8764 11.3906 3.18977 11.0356C2.50313 10.6806 1.91627 10.2229 1.42917 9.6626C0.942071 9.10226 0.570523 8.45741 0.314526 7.72805C0.0585292 6.99869 -0.0413244 6.23153 0.0149648 5.42655C0.071254 4.62157 0.276904 3.87576 0.631916 3.18913C0.986927 2.5025 1.4446 1.91564 2.00495 1.42854C2.56529 0.941438 3.21014 0.56989 3.9395 0.313893C4.66885 0.0578965 5.43602 -0.0419572 6.241 0.0143321C7.04598 0.0706213 7.79178 0.276272 8.47842 0.631283C9.16505 0.986295 9.75191 1.44397 10.239 2.00432C10.7261 2.56466 11.0977 3.20951 11.3537 3.93886C11.6097 4.66822 11.7095 5.43539 11.6532 6.24037C11.5969 7.04535 11.3913 7.79115 11.0363 8.47778C10.6813 9.16441 10.2236 9.75128 9.66323 10.2384C9.10289 10.7255 8.45804 11.097 7.72868 11.353C6.99933 11.609 6.23216 11.7089 5.42718 11.6526ZM5.50856 10.4888C6.80817 10.5796 7.94049 10.2056 8.90552 9.36673C9.87056 8.52784 10.3985 7.45859 10.4894 6.15899C10.5803 4.85938 10.2063 3.72706 9.36737 2.76203C8.52847 1.79699 7.45922 1.26903 6.15962 1.17816C4.86001 1.08728 3.72769 1.46129 2.76266 2.30018C1.79762 3.13908 1.26967 4.20832 1.17879 5.50793C1.08791 6.80753 1.46192 7.93985 2.30081 8.90489C3.13971 9.86992 4.20896 10.3979 5.50856 10.4888Z" fill="var(--primary)" />
              </svg>
              {t("loginTestimonials.badgeLabel")}
            </span>
          </div>

          {/* aspas */}
          <img
            src="/quote-marks.png"
            alt=""
            draggable={false}
            style={{ height: 24, width: "auto", userSelect: "none", alignSelf: "flex-start" }}
          />

          {/* quote */}
          <p
            className="font-medium leading-relaxed"
            style={{ fontSize: "clamp(12px, 1.1vw, 16px)", color: "var(--foreground)" }}
          >
            {t(`loginTestimonials.slides.${slide.slideKey}.quote`)}
          </p>

          {/* author */}
          <div className="flex items-center gap-3">
            {slide.avatar ? (
              <img
                src={slide.avatar}
                alt={slide.author}
                className="rounded-full object-cover"
                style={{ width: "clamp(24px, 2vw, 32px)", height: "clamp(24px, 2vw, 32px)" }}
              />
            ) : (
              <div
                className="rounded-full flex items-center justify-center font-bold"
                style={{
                  width: "clamp(24px, 2vw, 32px)",
                  height: "clamp(24px, 2vw, 32px)",
                  fontSize: "clamp(9px, 0.7vw, 12px)",
                  background: "var(--secondary)",
                  color: "var(--primary)",
                }}
              >
                {slide.author[0]}
              </div>
            )}
            <div className="flex flex-col">
              <span
                className="font-bold uppercase tracking-wide"
                style={{ fontSize: "clamp(9px, 0.8vw, 12px)", color: "var(--primary)" }}
              >
                {t(`loginTestimonials.slides.${slide.slideKey}.title`)}
              </span>
              <span style={{ ...poppins, fontSize: "clamp(9px, 0.8vw, 12px)", color: "var(--muted-foreground)" }}>
                {slide.author}
              </span>
            </div>
          </div>
        </div>

        {/* dots */}
        <div className="flex gap-2 mt-6">
          {SLIDES.map((s, i) => (
            <button
              key={s.id}
              onClick={() => goTo(i)}
              aria-label={`Slide ${i + 1}`}
              className="rounded-full border-none cursor-pointer transition-all duration-300"
              style={{
                width: i === current ? 24 : 8,
                height: 8,
                backgroundColor: i === current ? "#57FEBB" : "oklch(0.45 0.20 292 / 0.3)",
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Form ──────────────────────────────────────────────────────────────────────

interface LoginFormProps {
  initialMode: "login" | "signup";
}

function LoginForm({ initialMode }: LoginFormProps) {
  const router = useRouter();
  const hostname = useClientHostname();
  const { t, locale, setLocale } = useI18n();
  const isTestEnvironment = useIsTestEnvironment();
  const signupProfileOptions = useMemo(
    () =>
      [
        {
          id: "school" as const,
          label: t("signup.profileSchools"),
          icon: schoolsIcon,
          disabled: !isTestEnvironment,
        },
        {
          id: "teacher" as const,
          label: t("signup.profileTeachers"),
          icon: teachersIcon,
          disabled: false,
        },
      ] as const,
    [t, isTestEnvironment],
  );
  const { currency: geoCurrency } = useGeoPricing();
  const [mode] = useState<"login" | "signup">(initialMode);
  const [signupStep, setSignupStep] = useState<"profile" | "form" | "verify" | "google-profile">("profile");
  const [googlePendingCredential, setGooglePendingCredential] = useState<string | null>(null);
  const [profile, setProfile] = useState<"school" | "teacher" | null>(null);
  const trackMetaRegistration = useMemo(
    () => createMetaTeacherRegistrationTracker(),
    [],
  );
  const [hoveredProfile, setHoveredProfile] = useState<"school" | "teacher" | null>(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  // Login sem senha: etapa "email" (só o email) → etapa "code" (código enviado
  // por email). O input de senha só aparece pelo botão discreto "entrar com senha".
  const [loginStep, setLoginStep] = useState<"email" | "code">("email");
  const [loginCode, setLoginCode] = useState("");
  const [showPasswordLogin, setShowPasswordLogin] = useState(false);
  const [loginCodeSending, setLoginCodeSending] = useState(false);
  const lastAutoSubmittedLoginCodeRef = useRef("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotSending, setForgotSending] = useState(false);
  const [forgotEmailValid, setForgotEmailValid] = useState(true);
  const [verificationCode, setVerificationCode] = useState("");
  const [createdUserId, setCreatedUserId] = useState("");
  const [createdWorldId, setCreatedWorldId] = useState("");
  const [countdown, setCountdown] = useState(0);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const forgotSendingRef = useRef(false);
  const lastAutoSubmittedCodeRef = useRef("");
  const [signupTouched, setSignupTouched] = useState<Set<string>>(new Set());

  function touchSignupField(field: string) {
    setSignupTouched(prev => new Set([...prev, field]));
  }

  const emailValid = /\S+@\S+\.\S+/.test(email.trim());
  const nameValid = firstName.trim().length >= 2;
  const lastNameValid = lastName.trim().length >= 2;
  // Cadastro simplificado: só o email é obrigatório (nome e senha ficam a cargo do backend).
  const canSubmit = emailValid;
  const canGoogleProfileSubmit = nameValid && lastNameValid && !!profile;

  useEffect(() => {
    // Google signup completion — user clicked Google on login page but had no account
    try {
      const googleRaw = sessionStorage.getItem("GAMEFIK_GOOGLE_PENDING_SIGNUP");
      if (googleRaw) {
        sessionStorage.removeItem("GAMEFIK_GOOGLE_PENDING_SIGNUP");
        const { credential, firstName: gFn, lastName: gLn, email: gEmail } = JSON.parse(googleRaw);
        setGooglePendingCredential(credential);
        setFirstName(gFn || "");
        setLastName(gLn || "");
        setEmail(gEmail || "");
        setSignupStep("google-profile");
        return;
      }
    } catch (error) {
      console.warn("Falha ao restaurar signup pendente do Google (sessionStorage):", error);
    }

    // Handle Apple redirect flow fallback (popup was blocked → page redirected to /auth/apple/callback → back here)
    try {
      const appleRaw = sessionStorage.getItem("GAMEFIK_APPLE_CALLBACK");
      if (appleRaw) {
        sessionStorage.removeItem("GAMEFIK_APPLE_CALLBACK");
        const { code, id_token } = JSON.parse(appleRaw);
        if (code && id_token) {
          setLoading(true);
          clearLocalStoragePreservingLocale();
          AuthService.loginWithApple({ code, id_token, preferrAccount: "6516e5c39c42f10008f6a393" })
            .then(async (response) => {
              if (!response.error && AuthService.isLogged()) {
                const userSettings = Settings.getSettings();
                const groups = userSettings?.accountsDetails?.[0]?.groups || [];
                const currentAccount = userSettings?.accountsDetails?.[0];
                const isNewManagerGlobal = Boolean(currentAccount?.["new-manager-global"]);
                if (isNewManagerGlobal) {
                  router.push("/chat-ia");
                } else if (groups.includes("admin") || groups.includes("financial_admin")) {
                  router.push("/dashboard");
                } else {
                  router.push("/details");
                }
              } else {
                toast.error("Conta não encontrada para este Apple ID");
                setLoading(false);
              }
            })
            .catch((error: any) => {
              toast.error(error?.message || error?.error || "Erro ao autenticar com Apple");
              setLoading(false);
            });
          return;
        }
      }
    } catch (error) {
      console.warn("Falha ao processar callback do Apple (sessionStorage):", error);
    }

    // Restore pending email-verification state across page refreshes
    // (a senha não é mais guardada: a verificação por código já devolve a sessão)
    try {
      const raw = sessionStorage.getItem("GAMEFIK_PENDING_VERIFY");
      if (raw) {
        const pending = JSON.parse(raw);
        if (pending.userId && pending.email) {
          setCreatedUserId(pending.userId);
          setCreatedWorldId(pending.worldId || "");
          setProfile(pending.profile || "teacher");
          setEmail(pending.email);
          setSignupStep("verify");
          // redirect to signup page if we're on login
          if (initialMode === "login") router.replace("/signup");
        }
      }
    } catch (error) {
      console.warn("Falha ao restaurar verificação de e-mail pendente (sessionStorage):", error);
    }

    if (initialMode === "signup") {
      const params = new URLSearchParams(window.location.search);
      const requestedProfile = params.get("profile");
      const host = window.location.hostname;
      if (requestedProfile === "teacher") {
        setProfile("teacher");
        setSignupStep("form");
      } else if (requestedProfile === "school" && isTestEnvironmentHost(host)) {
        setProfile("school");
        setSignupStep("form");
      }
    }

    // Auto-login via URL params (internal tooling flow)
    // O clear só roda nesse fluxo: limpar incondicionalmente aqui apagava o
    // TOKEN_LOGIN de usuário logado que caía em /login e era redirecionado.
    setTimeout(() => {
      const params = new URLSearchParams(window.location.search);
      const e = params.get("email");
      const p = params.get("password");
      if (e && p) {
        clearLocalStoragePreservingLocale();
        setEmail(e);
        setPassword(p);
        setTimeout(() => submit(e, p), 1000);
      }
    }, 1000);

    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  async function redirectAfterLogin() {
    const userSettings = Settings.getSettings();
    const preferredLang = userSettings?.userData?.preferredLanguage;
    if (preferredLang) {
      setLocale(normalizeLocale(preferredLang));
    }

    const currentAccount = userSettings?.accountsDetails?.[0];

    // Professores NMG que ainda não completaram o onboarding precisam ser
    // forçados ao wizard mesmo em re-login (ex: abandonaram e voltaram).
    // Sem isso, eles caem em /chat-ia e o OnboardingGate não os redireciona
    // porque /chat-ia está nas rotas excluídas (precisa ficar excluída pra
    // não atrapalhar a celebração final do próprio wizard).
    const isTeacherNMG =
      Boolean(currentAccount?.["new-manager-global"]) &&
      currentAccount?.signupProfile === "teacher";
    let teacherOnboardingDone = false;
    if (isTeacherNMG) {
      const completedLocal = hasLocalOnboardingCompletion();
      const { completed: completedRemote, checkFailed: remoteCheckFailed } =
        await readRemoteOnboardingCompletion(loginClient);
      // Concluído OU indeterminado: nos dois casos não se força o wizard abaixo.
      teacherOnboardingDone = completedLocal || completedRemote || remoteCheckFailed;
      // NUNCA força o wizard sem evidência POSITIVA de "não concluído". O
      // submit() acabou de rodar clearLocalStoragePreservingLocale(), então
      // completedLocal é praticamente sempre false aqui — a leitura remota é a
      // única proteção de quem já concluiu. E `authenticatedRequest` (instância)
      // NUNCA rejeita: em erro ele RESOLVE `{ error }`, o que virava
      // completedRemote=false e reenviava um professor já onboardado pro wizard
      // inteiro por causa de um único hiccup de rede (2026-07-29).
      // Falha de leitura => trata como desconhecido: segue o fluxo normal e
      // deixa o OnboardingGate decidir quando tiver uma leitura confiável.
      if (!completedLocal && !completedRemote && !remoteCheckFailed) {
        try {
          // B2C Free Flow: entra no Free (sem trial automático).
          await loginClient.completeSignupFreePlan("teacher");
          Settings.unloadSettings();
          await Settings.loadSettings();
        } catch (err: any) {
          console.error("[completeSignupFreePlan] pre-onboarding redirect failed:", err?.message || err?.error || err);
        }
        router.push(await resolveOnboardingRedirectTarget());
        return;
      }
    }

    if (currentAccount?.["new-manager-global"] && !currentAccount?.signupPlanCompletedAt) {
      if (currentAccount?.signupProfile === "teacher") {
        try {
          // B2C Free Flow: entra no Free e vai ao onboarding (A/B), não mais trial+chat-ia.
          await loginClient.completeSignupFreePlan("teacher");
          Settings.unloadSettings();
          await Settings.loadSettings();
          // `signupPlanCompletedAt` ausente NÃO implica onboarding pendente: quem
          // já concluiu (ou cuja leitura falhou) só precisa do plano Free criado
          // acima e segue para o app. Antes, este ramo mandava para o wizard sem
          // checar conclusão nenhuma — um segundo caminho de bounce-back,
          // independente do guard logo acima (2026-07-29).
          router.push(teacherOnboardingDone ? "/chat-ia" : await resolveOnboardingRedirectTarget());
          return;
        } catch (err: any) {
          console.error("[completeSignupFreePlan] login redirectAfterLogin failed:", err?.message || err?.error || err);
          router.push("/signup/plans");
          return;
        }
      }
      router.push(getSchoolSignupContinuation(currentAccount, userSettings?.setup_completed));
      return;
    }
    const groups = currentAccount?.groups || [];
    const isNewManagerGlobal = Boolean(currentAccount?.["new-manager-global"]);
    if (isNewManagerGlobal) {
      router.push("/chat-ia");
    } else if (groups.includes("admin") || groups.includes("financial_admin")) {
      router.push("/dashboard");
    } else {
      router.push("/details");
    }
  }

  async function submit(replaceEmail?: string, replacePassword?: string) {
    clearLocalStoragePreservingLocale();
    setLoading(true);
    try {
      const response = await AuthService.login({
        username: replaceEmail ?? email,
        password: replacePassword ?? password,
        preferrAccount: "6516e5c39c42f10008f6a393",
      });
      if (!response.error && AuthService.isLogged()) {
        identifyFromToken(response.token);
        Analytics.loginCompleted({ login_method: "email", is_returning: true });
        await redirectAfterLogin();
        return;
      }
      Analytics.loginFailed({ login_method: "email", error_code: "wrong_password" });
      toast.error(t("signup.toastLoginError"));
    } catch {
      Analytics.loginFailed({ login_method: "email", error_code: "network_error" });
      toast.error(t("signup.toastLoginError"));
    }
    setLoading(false);
  }

  /**
   * Etapa 1 do login: só o email. Logins internos gfk-/gfk=/gfk+ entram direto
   * (a senha é ignorada ou derivável no cliente); email comum recebe um código
   * de 6 dígitos e avança para a etapa de verificação.
   */
  async function handleLoginContinue() {
    const trimmed = email.trim();
    const gfkPassword = getGfkDirectPassword(trimmed);
    if (gfkPassword !== null) {
      await submit(trimmed, gfkPassword);
      return;
    }

    const normalized = trimmed.toLowerCase();
    if (!Validator.email(normalized)) {
      toast.error(t("loginCode.invalidEmail"));
      return;
    }

    setLoginCodeSending(true);
    try {
      // O backend prioriza o preferredLanguage salvo do usuário; o locale da UI
      // é só fallback para quem nunca definiu idioma.
      await loginClient.sendLoginCode(normalized, locale);
      setLoginCode("");
      lastAutoSubmittedLoginCodeRef.current = "";
      setShowPasswordLogin(false);
      setLoginStep("code");
      startCountdown(60);
      toast.success(t("loginCode.sentToast"));
    } catch (error: any) {
      const message = extractApiErrorMessage(error);
      Analytics.loginFailed({ login_method: "email_code", error_code: error?.code === 404 ? "user_not_found" : "send_code_failed" });
      if (error?.code === 404) {
        toast.error(t("loginCode.userNotFound"));
      } else {
        toast.error(message || t("loginCode.sendError"));
      }
    } finally {
      setLoginCodeSending(false);
    }
  }

  /** Etapa 2 do login: valida o código recebido por email e entra sem senha. */
  async function handleLoginCodeVerify() {
    const code = loginCode.trim();
    if (code.length < 6) {
      toast.error(t("signup.toastEnterCode"));
      return;
    }
    clearLocalStoragePreservingLocale();
    setLoading(true);
    try {
      const response = await AuthService.loginWithEmailCode({
        email: email.trim().toLowerCase(),
        code,
      });
      if (!response.error && AuthService.isLogged()) {
        identifyFromToken(response.token);
        Analytics.loginCompleted({ login_method: "email_code", is_returning: true });
        await redirectAfterLogin();
        return;
      }
      Analytics.loginFailed({ login_method: "email_code", error_code: "invalid_code" });
      toast.error(extractApiErrorMessage(response) || t("loginCode.invalidCode"));
    } catch (error: any) {
      Analytics.loginFailed({ login_method: "email_code", error_code: "network_error" });
      toast.error(extractApiErrorMessage(error) || t("loginCode.invalidCode"));
    }
    setLoading(false);
  }

  async function resendLoginCode() {
    if (countdown > 0 || loginCodeSending) return;
    setLoginCodeSending(true);
    try {
      await loginClient.sendLoginCode(email.trim().toLowerCase(), locale);
      lastAutoSubmittedLoginCodeRef.current = "";
      startCountdown(60);
      toast.success(t("signup.toastResendSuccess"));
    } catch (error: any) {
      toast.error(extractApiErrorMessage(error) || t("signup.toastResendError"));
    } finally {
      setLoginCodeSending(false);
    }
  }

  async function submitForgotPassword() {
    if (forgotSendingRef.current) return;
    forgotSendingRef.current = true;

    const addr = email.trim().toLowerCase();
    const valid = Validator.email(addr);
    setForgotEmailValid(valid);
    if (!valid) {
      toast.error(t("forgotPassword.invalidEmail"));
      forgotSendingRef.current = false;
      return;
    }

    setForgotSending(true);
    try {
      const dbLocale = await loginClient.getPreferredLanguageByEmail(addr);
      const resolvedLocale = dbLocale ?? locale;
      await loginClient.forgotPassword(addr, resolvedLocale);
      toast.success(t("forgotPassword.successMessage"));
      setShowForgotPassword(false);
    } catch (error: any) {
      const msg = String(error?.message || error?.error || "");
      if (msg.includes("User not found")) {
        toast.error(t("forgotPassword.userNotFound"));
      } else if (msg.includes("User not verified")) {
        toast.error(t("forgotPassword.userNotVerified"));
      } else {
        toast.error(t("forgotPassword.genericError"));
      }
    } finally {
      forgotSendingRef.current = false;
      setForgotSending(false);
    }
  }

  function startCountdown(seconds: number) {
    if (countdownRef.current) clearInterval(countdownRef.current);
    setCountdown(seconds);
    countdownRef.current = setInterval(() => {
      setCountdown(current => {
        if (current <= 1) {
          if (countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
          }
          return 0;
        }
        return current - 1;
      });
    }, 1000);
  }

  async function handleSignup() {
    if (!canSubmit) {
      toast.error(t("signup.emailInvalid"));
      return;
    }
    setLoading(true);
    const signupStartTime = Date.now();
    Analytics.signupStarted({ signup_method: "email", profile });
    try {
      // Cadastro simplificado: só o email vai ao backend (nome inferido, senha gerada lá).
      const response = await loginClient.signupRegister({
        profile: profile!,
        email: email.trim().toLowerCase(),
        locale,
      });
      setCreatedUserId(response.userId);
      setCreatedWorldId(response.worldId);
      try { sessionStorage.setItem("GAMEFIK_SIGNUP_START_TIME", String(signupStartTime)); } catch { /* noop */ }
      if (response.verified && response.token) {
        await AuthService.setToken(response.token);
        await persistPostHogSignupAttribution({
          client: posthog,
          identifyFromToken,
          token: response.token,
          email: email.trim().toLowerCase(),
          papel: profile!,
        });
        const startTime = Number(sessionStorage.getItem("GAMEFIK_SIGNUP_START_TIME") || 0);
        sessionStorage.removeItem("GAMEFIK_SIGNUP_START_TIME");
        Analytics.signupCompleted({
          signup_method: "email",
          language: locale,
          signup_duration_seconds: startTime ? Math.round((Date.now() - startTime) / 1000) : undefined,
        });
        localStorage.setItem("GAMEFIK_SIGNUP_PROFILE", profile!);
        toast.success(t("signup.toastVerified"));
        trackMetaRegistration(profile);
        await redirectAfterSignup(profile!);
        setLoading(false);
        return;
      }

      setSignupStep("verify");
      startCountdown(60);
      sessionStorage.setItem("GAMEFIK_PENDING_VERIFY", JSON.stringify({
        userId: response.userId,
        worldId: response.worldId,
        email: email.trim().toLowerCase(),
        profile,
      }));
      toast.success(t("signup.toastAccountCreated"));
    } catch (error: any) {
      Analytics.signupFailed({ signup_method: "email", error_code: error?.code || "unknown", error_message: error?.message || error?.error });
      toast.error(error?.message || error?.error || t("signup.toastSignupError"));
    }
    setLoading(false);
  }

  async function redirectAfterSignup(signedProfile: "teacher" | "school") {
    if (signedProfile === "school") {
      router.push("/signup/school-onboarding");
      return;
    }
    // Teacher flow — B2C Free Flow: entra no Free (sem trial automático).
    try {
      await loginClient.completeSignupFreePlan("teacher");
      Settings.unloadSettings();
      await Settings.loadSettings();
      // Vai direto ao wizard (variante A/B) pra evitar o flash da /home enquanto o
      // OnboardingGate carregava settings + users/info pra redirecionar.
      router.push(await resolveOnboardingRedirectTarget());
      return;
    } catch (err: any) {
      console.error("[completeSignupFreePlan] login redirectAfterSignup failed:", err?.message || err?.error || err);
      router.push("/signup/plans");
      return;
    }
  }

  async function handleVerify() {
    if (!createdUserId || verificationCode.trim().length < 4) {
      toast.error(t("signup.toastEnterCode"));
      return;
    }
    setLoading(true);
    try {
      // Verificação sem senha: o código já devolve a sessão (auto-login).
      const response = await loginClient.verifySignupCode(
        createdUserId,
        verificationCode.trim(),
      );
      sessionStorage.removeItem("GAMEFIK_PENDING_VERIFY");
      await AuthService.setToken(response.token);
      await persistPostHogSignupAttribution({
        client: posthog,
        identifyFromToken,
        token: response.token,
        email: email.trim().toLowerCase(),
        papel: profile!,
      });
      const startTime = Number(sessionStorage.getItem("GAMEFIK_SIGNUP_START_TIME") || 0);
      sessionStorage.removeItem("GAMEFIK_SIGNUP_START_TIME");
      Analytics.signupCompleted({
        signup_method: "email",
        language: locale,
        signup_duration_seconds: startTime ? Math.round((Date.now() - startTime) / 1000) : undefined,
      });
      localStorage.setItem("GAMEFIK_SIGNUP_PROFILE", profile!);
      toast.success(t("signup.toastVerified"));
      trackMetaRegistration(profile);
      await redirectAfterSignup(profile!);
    } catch (error: any) {
      Analytics.signupFailed({ signup_method: "email", error_code: error?.code || "invalid_code", error_message: error?.message || error?.error });
      toast.error(error?.message || error?.error || t("signup.toastInvalidCode"));
    }
    setLoading(false);
  }

  // Auto-login: ao completar os 6 dígitos do código, verifica sem o usuário clicar em nada.
  // O ref evita reenviar o mesmo código após uma tentativa que falhou.
  useEffect(() => {
    const code = verificationCode.trim();
    if (signupStep !== "verify" || loading || !createdUserId) return;
    if (!/^\d{6}$/.test(code)) return;
    if (lastAutoSubmittedCodeRef.current === code) return;
    lastAutoSubmittedCodeRef.current = code;
    void handleVerify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationCode, signupStep, loading, createdUserId]);

  // Auto-login no código de login: ao completar os 6 dígitos, verifica sozinho.
  // O ref evita reenviar o mesmo código após uma tentativa que falhou.
  useEffect(() => {
    const code = loginCode.trim();
    if (mode !== "login" || loginStep !== "code" || showPasswordLogin || loading) return;
    if (!/^\d{6}$/.test(code)) return;
    if (lastAutoSubmittedLoginCodeRef.current === code) return;
    lastAutoSubmittedLoginCodeRef.current = code;
    void handleLoginCodeVerify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginCode, loginStep, showPasswordLogin, loading, mode]);

  async function resendCode() {
    if (!createdUserId || countdown > 0) return;
    setLoading(true);
    try {
      await loginClient.sendCode(createdUserId);
      startCountdown(60);
      toast.success(t("signup.toastResendSuccess"));
    } catch (error: any) {
      toast.error(error?.message || error?.error || t("signup.toastResendError"));
    }
    setLoading(false);
  }

  async function handleGoogleLogin(credentialResponse: CredentialResponse) {
    if (!credentialResponse?.credential) {
      toast.error(t("signup.toastGoogleError"));
      return;
    }
    clearLocalStoragePreservingLocale();
    setLoading(true);
    try {
      if (mode === "signup") {
        let signupError: any = null;
        try {
          const decodedForSignup = decodeGoogleJwt(credentialResponse.credential);
          const signupFullName = [
            decodedForSignup?.given_name || decodedForSignup?.name?.split(" ")[0] || "",
            decodedForSignup?.family_name || decodedForSignup?.name?.split(" ").slice(1).join(" ") || "",
          ].filter(Boolean).join(" ");
          const response = await loginClient.signupRegisterWithGoogle({
            profile: profile!,
            credential: credentialResponse.credential,
            fullName: signupFullName || undefined,
            locale,
          });
          await AuthService.setToken(response.token);
          await persistPostHogSignupAttribution({
            client: posthog,
            identifyFromToken,
            token: response.token,
            email: String(response.email || decodedForSignup?.email || email).trim().toLowerCase(),
            papel: profile!,
          });
          Analytics.signupCompleted({ signup_method: "google", language: locale });
          localStorage.setItem("GAMEFIK_SIGNUP_PROFILE", profile!);
          toast.success(t("signup.toastVerified"));
          trackMetaRegistration(profile);
          await redirectAfterSignup(profile!);
          return;
        } catch (err) {
          signupError = err;
        }
        // Email já tem conta — tenta logar
        try {
          const loginResponse = await AuthService.loginWithGoogle({
            credential: credentialResponse.credential,
            preferrAccount: "6516e5c39c42f10008f6a393",
          });
          if (!loginResponse.error && AuthService.isLogged()) {
            await redirectAfterLogin();
            return;
          }
        } catch (error) {
          console.warn("Fallback de login após signup Google falhou; propagando erro original:", error);
        }
        throw signupError;
      }
      const response = await AuthService.loginWithGoogle({
        credential: credentialResponse.credential,
        preferrAccount: "6516e5c39c42f10008f6a393",
      });
      if (!response.error && AuthService.isLogged()) {
        identifyFromToken(response.token);
        Analytics.loginCompleted({ login_method: "google", is_returning: true });
        await redirectAfterLogin();
        return;
      }
      // No account found — redirect to signup with Google data pre-filled
      const decoded = decodeGoogleJwt(credentialResponse.credential);
      sessionStorage.setItem("GAMEFIK_GOOGLE_PENDING_SIGNUP", JSON.stringify({
        credential: credentialResponse.credential,
        firstName: decoded?.given_name || decoded?.name?.split(" ")[0] || "",
        lastName: decoded?.family_name || decoded?.name?.split(" ").slice(1).join(" ") || "",
        email: decoded?.email || "",
      }));
      setLoading(false);
      router.push("/signup");
      return;
    } catch (error: any) {
      if (mode === "signup") {
        Analytics.signupFailed({ signup_method: "google", error_code: error?.code || "unknown", error_message: error?.message || error?.error });
      } else {
        Analytics.loginFailed({ login_method: "google", error_code: error?.code || "user_not_found" });
      }
      toast.error(error?.message || error?.error || t("signup.toastGoogleError"));
    }
    setLoading(false);
  }

  async function handleGoogleSignupComplete() {
    if (!googlePendingCredential || !profile) return;
    setLoading(true);
    try {
      const fullName = [firstName.trim(), lastName.trim()].filter(Boolean).join(" ");
      let signupError: any = null;
      try {
        const response = await loginClient.signupRegisterWithGoogle({
          profile,
          credential: googlePendingCredential,
          fullName,
          locale,
        });
        await AuthService.setToken(response.token);
        await persistPostHogSignupAttribution({
          client: posthog,
          identifyFromToken,
          token: response.token,
          email: String(response.email || email).trim().toLowerCase(),
          papel: profile,
        });
        Analytics.signupCompleted({ signup_method: "google", language: locale });
        localStorage.setItem("GAMEFIK_SIGNUP_PROFILE", profile);
        toast.success(t("signup.toastVerified"));
        trackMetaRegistration(profile);
        await redirectAfterSignup(profile);
        return;
      } catch (err) {
        signupError = err;
      }
      // Email já tem conta — tenta logar
      try {
        const loginResponse = await AuthService.loginWithGoogle({
          credential: googlePendingCredential,
          preferrAccount: "6516e5c39c42f10008f6a393",
        });
        if (!loginResponse.error && AuthService.isLogged()) {
          await redirectAfterLogin();
          return;
        }
      } catch (error) {
        console.warn("Fallback de login após signup falhou; propagando erro original:", error);
      }
      throw signupError;
    } catch (error: any) {
      Analytics.signupFailed({ signup_method: "google", error_code: error?.code || "unknown", error_message: error?.message || error?.error });
      toast.error(error?.message || error?.error || t("signup.toastGoogleError"));
    }
    setLoading(false);
  }

  async function handleAppleLogin() {
    const clientId = "com.gamefik.manager.web";
    const redirectURI = "https://hmg-manager.gamefik.com/auth/apple/callback";

    clearLocalStoragePreservingLocale();
    setLoading(true);
    try {
      await initAppleAuth({ clientId, redirectURI });
      const appleResponse = await signInWithApple();

      if (!appleResponse?.id_token || !appleResponse?.code) {
        toast.error(t("signup.toastAppleError"));
        setLoading(false);
        return;
      }

      if (mode === "signup") {
        const response = await loginClient.signupRegisterWithApple({
          profile: profile!,
          code: appleResponse.code,
          id_token: appleResponse.id_token,
          locale,
        });
        await AuthService.setToken(response.token);
        await persistPostHogSignupAttribution({
          client: posthog,
          identifyFromToken,
          token: response.token,
          email: String(response.email || email).trim().toLowerCase(),
          papel: profile!,
        });
        Analytics.signupCompleted({ signup_method: "apple", language: locale });
        localStorage.setItem("GAMEFIK_SIGNUP_PROFILE", profile!);
        toast.success(t("signup.toastVerified"));
        trackMetaRegistration(profile);
        await redirectAfterSignup(profile!);
        return;
      }

      const response = await AuthService.loginWithApple({
        code: appleResponse.code,
        id_token: appleResponse.id_token,
        preferrAccount: "6516e5c39c42f10008f6a393",
      });
      if (!response.error && AuthService.isLogged()) {
        identifyFromToken(response.token);
        Analytics.loginCompleted({ login_method: "apple", is_returning: true });
        await redirectAfterLogin();
        return;
      }
      Analytics.loginFailed({ login_method: "apple", error_code: "user_not_found" });
      toast.error(t("signup.toastAppleNotFound"));
    } catch (error: any) {
      if (error?.message?.includes("popup") || error?.error === "popup_closed_by_user") {
        setLoading(false);
        return;
      }
      if (mode === "signup") {
        Analytics.signupFailed({ signup_method: "apple", error_code: error?.code || "unknown", error_message: error?.message || error?.error });
      } else {
        Analytics.loginFailed({ login_method: "apple", error_code: error?.code || "network_error" });
      }
      toast.error(error?.message || error?.error || t("signup.toastAppleError"));
    }
    setLoading(false);
  }

  if (loading) return <RocketLoading />;

  return (
    <>
      <div className="flex flex-col gap-6 w-full p-8">
        {/* tab switcher */}
        {/* <div className="flex rounded-full gap-2 p-1.5" style={{ backgroundColor: "#F2E2FF", border: "1px solid #BAA4D21A" }}>
          {(["login", "signup"] as const).map(tab => (
            <button
              key={tab}
              type="button"
              onClick={() => router.push(tab === "login" ? "/login" : "/signup")}
              className="flex-1 rounded-full font-normal border-none cursor-pointer transition-all duration-200"
              style={{
                ...sora,
                height: 48,
                fontSize: 16,
                fontWeight: 400,
                backgroundColor: mode === tab ? "var(--primary)" : "transparent",
                color: mode === tab ? "var(--primary-foreground)" : "var(--primary)",
                boxShadow: mode === tab ? "var(--shadow-card)" : "none",
              }}
            >
              {tab === "login" ? t("signup.tabLogin") : t("signup.tabSignup")}
            </button>
          ))}
        </div> */}

        {/* heading */}
        {(() => {
          const screen = showForgotPassword ? "forgot"
            : mode === "login" ? (loginStep === "code" ? "login-code" : "login")
            : signupStep === "profile" ? "profile"
            : signupStep === "google-profile" ? "google-profile"
            : signupStep === "verify" ? "verify"
            : "form";

          const headings = {
            forgot:         t("forgotPassword.heading"),
            login:          t("signup.loginHeading"),
            "login-code":   t("loginCode.verifyHeading"),
            profile:        t("signup.profileHeading"),
            "google-profile": t("signup.profileHeading"),
            verify:         t("signup.verifyHeading"),
            form:           t("signup.formHeading"),
          } as const;

          const subtitles = {
            forgot:         t("forgotPassword.subtitle"),
            login:          t("signup.loginSubheading"),
            "login-code":   t("loginCode.verifySubtitle", { email: email.trim() }),
            profile:        t("signup.profileSubheading"),
            "google-profile": t("signup.profileSubheading"),
            verify:         t("signup.verifySubheading", { email }),
            form:           t("signup.formSubheading"),
          } as const;

          return (
            <div className="flex flex-col gap-1">
              <h1
                className="m-0"
                style={{ ...sora, fontSize: 36, fontWeight: 800, color: "var(--primary)", lineHeight: "40px", letterSpacing: "-0.9px", textAlign: "center" }}
              >
                {headings[screen]}
              </h1>
              <p
                className="m-0"
                style={{ ...sora, fontSize: 20, fontWeight: 400, color: "var(--foreground)", lineHeight: "20px", textAlign: "center" }}
              >
                {subtitles[screen]}
              </p>
            </div>
          );
        })()}

        {/* seleção de perfil (signup step 1) */}
        {mode === "signup" && signupStep === "profile" && (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-3">
              {signupProfileOptions.map(opt => {
                const isActive = !opt.disabled && (profile === opt.id || hoveredProfile === opt.id);
                return (
                  <button
                    key={opt.id}
                    data-testid="signup-profile-option"
                    type="button"
                    disabled={opt.disabled}
                    onClick={() => { if (opt.disabled) return; setProfile(opt.id); setSignupStep("form"); }}
                    onMouseEnter={() => { if (!opt.disabled) setHoveredProfile(opt.id); }}
                    onMouseLeave={() => { if (!opt.disabled) setHoveredProfile(null); }}
                    className="flex flex-col items-center gap-2 transition-all duration-150"
                    style={{
                      borderRadius: 24,
                      border: `2px solid ${isActive ? "var(--primary)" : "#D7DCF4"}`,
                      backgroundColor: isActive ? "var(--primary)" : "#ffffff",
                      padding: "1rem 0.75rem",
                      userSelect: "none",
                      cursor: opt.disabled ? "not-allowed" : "pointer",
                      opacity: opt.disabled ? 0.5 : 1,
                    }}
                  >
                    <Image src={opt.icon} alt={opt.label} width={92} height={92} />
                    <span style={{ ...sora, fontSize: 20, fontWeight: 700, color: isActive ? "#ffffff" : "var(--foreground)" }}>
                      {opt.label}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* <Button
              type="button"
              className="w-full h-12 gap-4"
              disabled={!profile}
              onClick={() => setSignupStep("form")}
              style={jakartaSans}
            >
              {t("signup.profileNext")}
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0.275 8.19442L4.475 3.99442C4.70833 3.76109 4.98333 3.59442 5.3 3.49442C5.61667 3.39442 5.94167 3.37775 6.275 3.44442L7.575 3.71942C6.675 4.78609 5.96667 5.75275 5.45 6.61942C4.93333 7.48609 4.43333 8.53609 3.95 9.76942L0.275 8.19442ZM5.4 10.4694C5.78333 9.26942 6.30417 8.13609 6.9625 7.06942C7.62083 6.00275 8.41667 5.00275 9.35 4.06942C10.8167 2.60275 12.4917 1.50692 14.375 0.781922C16.2583 0.0569215 18.0167 -0.163912 19.65 0.119421C19.9333 1.75275 19.7167 3.51109 19 5.39442C18.2833 7.27775 17.1917 8.95275 15.725 10.4194C14.8083 11.3361 13.8083 12.1319 12.725 12.8069C11.6417 13.4819 10.5 14.0111 9.3 14.3944L5.4 10.4694ZM12.3 7.46942C12.6833 7.85275 13.1542 8.04442 13.7125 8.04442C14.2708 8.04442 14.7417 7.85275 15.125 7.46942C15.5083 7.08609 15.7 6.61525 15.7 6.05692C15.7 5.49859 15.5083 5.02775 15.125 4.64442C14.7417 4.26109 14.2708 4.06942 13.7125 4.06942C13.1542 4.06942 12.6833 4.26109 12.3 4.64442C11.9167 5.02775 11.725 5.49859 11.725 6.05692C11.725 6.61525 11.9167 7.08609 12.3 7.46942ZM11.6 19.4944L10 15.8194C11.2333 15.3361 12.2875 14.8361 13.1625 14.3194C14.0375 13.8028 15.0083 13.0944 16.075 12.1944L16.325 13.4944C16.3917 13.8278 16.375 14.1569 16.275 14.4819C16.175 14.8069 16.0083 15.0861 15.775 15.3194L11.6 19.4944ZM1.875 13.6694C2.45833 13.0861 3.16667 12.7903 4 12.7819C4.83333 12.7736 5.54167 13.0611 6.125 13.6444C6.70833 14.2278 7 14.9361 7 15.7694C7 16.6028 6.70833 17.3111 6.125 17.8944C5.70833 18.3111 5.0125 18.6694 4.0375 18.9694C3.0625 19.2694 1.71667 19.5361 0 19.7694C0.233333 18.0528 0.5 16.7111 0.8 15.7444C1.1 14.7778 1.45833 14.0861 1.875 13.6694Z" fill="white" />
              </svg>
            </Button> */}

            <p className="text-center m-0" style={{ ...sora, fontSize: 16, fontWeight: 300, color: "var(--muted-foreground)" }}>
              {t("signup.hasAccount")}{" "}
              <button
                type="button"
                onClick={() => router.push("/login")}
                className="border-none bg-transparent cursor-pointer hover:underline p-0"
                style={{ ...sora, fontSize: 16, fontWeight: 700, color: "var(--primary)" }}
              >
                {t("signup.loginLink")}
              </button>
            </p>
          </div>
        )}

        {/* ── Google signup completion (veio do login sem conta) ── */}
        {mode === "signup" && signupStep === "google-profile" && (
          <div className="flex flex-col gap-4">
            {/* Seleção de perfil — clique apenas seleciona, não avança */}
            <div className="grid grid-cols-2 gap-3">
              {signupProfileOptions.map(opt => {
                const isActive = !opt.disabled && (profile === opt.id || hoveredProfile === opt.id);
                return (
                  <button
                    key={opt.id}
                    type="button"
                    disabled={opt.disabled}
                    onClick={() => { if (opt.disabled) return; setProfile(opt.id); }}
                    onMouseEnter={() => { if (!opt.disabled) setHoveredProfile(opt.id); }}
                    onMouseLeave={() => { if (!opt.disabled) setHoveredProfile(null); }}
                    className="flex flex-col items-center gap-2 transition-all duration-150"
                    style={{
                      borderRadius: 24,
                      border: `2px solid ${isActive ? "var(--primary)" : "#D7DCF4"}`,
                      backgroundColor: isActive ? "var(--primary)" : "#ffffff",
                      padding: "1rem 0.75rem",
                      userSelect: "none",
                      cursor: opt.disabled ? "not-allowed" : "pointer",
                      opacity: opt.disabled ? 0.5 : 1,
                    }}
                  >
                    <Image src={opt.icon} alt={opt.label} width={92} height={92} />
                    <span style={{ ...sora, fontSize: 20, fontWeight: 700, color: isActive ? "#ffffff" : "var(--foreground)" }}>
                      {opt.label}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Nome + Sobrenome */}
            <div className="flex gap-3">
              <div className="flex flex-col gap-1.5 flex-1">
                <label htmlFor="auth-first-name" style={{ ...sora, fontSize: 12, fontWeight: 400, color: "var(--foreground)" }}>{t("signup.firstName")}</label>
                <input
                  id="auth-first-name"
                  type="text"
                  autoComplete="given-name"
                  value={firstName}
                  onChange={e => setFirstName(e.target.value)}
                  placeholder={t("signup.firstNamePlaceholder")}
                  style={inputBase}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                />
              </div>
              <div className="flex flex-col gap-1.5 flex-1">
                <label htmlFor="auth-last-name" style={{ ...sora, fontSize: 12, fontWeight: 400, color: "var(--foreground)" }}>{t("signup.lastName")}</label>
                <input
                  id="auth-last-name"
                  type="text"
                  autoComplete="family-name"
                  value={lastName}
                  onChange={e => setLastName(e.target.value)}
                  placeholder={t("signup.lastNamePlaceholder")}
                  style={inputBase}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                />
              </div>
            </div>

            {/* Email — somente leitura (vem do Google) */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="auth-profile-email" style={{ ...sora, fontSize: 12, fontWeight: 400, color: "var(--foreground)" }}>{t("signup.email")}</label>
              <div className="relative">
                <input
                  id="auth-profile-email"
                  type="email"
                  readOnly
                  value={email}
                  style={{ ...inputBase, backgroundColor: "rgba(66,80,102,0.06)", color: "var(--muted-foreground)", cursor: "default" }}
                />
                <svg className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--muted-foreground)" }} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect width="20" height="16" x="2" y="4" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                </svg>
              </div>
            </div>

            {/* Submit */}
            <Button
              type="button"
              className="w-full h-12 gap-4"
              disabled={!canGoogleProfileSubmit}
              onClick={handleGoogleSignupComplete}
              style={jakartaSans}
            >
              {t("signup.signupSubmit")}
              <RocketSvg />
            </Button>

            <p className="text-center m-0" style={{ ...sora, fontSize: 16, fontWeight: 300, color: "var(--muted-foreground)" }}>
              {t("signup.hasAccount")}{" "}
              <button
                type="button"
                onClick={() => router.push("/login")}
                className="border-none bg-transparent cursor-pointer hover:underline p-0"
                style={{ ...sora, fontSize: 16, fontWeight: 700, color: "var(--primary)" }}
              >
                {t("signup.loginLink")}
              </button>
            </p>
          </div>
        )}

        {/* social buttons — login (etapa de email) ou signup no form step */}
        {((mode === "login" && loginStep === "email" && !showForgotPassword) || (mode === "signup" && signupStep === "form")) && (
          <div className="flex flex-col gap-3">
            {/* Google + Apple — mesma largura, centralizados */}
            <div className="flex flex-col items-center gap-3 w-full">
              <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
                <GoogleLogin
                  onSuccess={handleGoogleLogin}
                  onError={() => toast.error(t("signup.toastGoogleError"))}
                  use_fedcm_for_button={true}
                  text={mode === "login" ? "signin_with" : "signup_with"}
                  shape="rectangular"
                  logo_alignment="center"
                  size="large"
                  width="360"
                  containerProps={{ style: { borderRadius: 8, overflow: "hidden", boxShadow: "var(--shadow-card)" } }}
                />
              </div>
              {/* Apple login */}
              {(
                <button
                  type="button"
                  onClick={handleAppleLogin}
                  className="font-normal flex items-center justify-center gap-3 border-none cursor-pointer transition-all hover:brightness-90"
                  style={{
                    ...sfPro,
                    width: 360,
                    height: 40,
                    borderRadius: 8,
                    fontSize: "0.875rem",
                    backgroundColor: "#000",
                    color: "#fff",
                    boxShadow: "var(--shadow-card)",
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701z" />
                  </svg>
                  {mode === "login" ? t("signup.appleLogin") : t("signup.appleSignup")}
                </button>
              )}
            </div>

            {/* divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px" style={{ backgroundColor: "var(--border)" }} />
              <span style={{ ...sora, fontSize: 12, fontWeight: 400, color: "#666666", letterSpacing: "0.1em" }}>
                {signupStep === "form" ? t("signup.dividerSignup") : t("signup.dividerLogin")}
              </span>
              <div className="flex-1 h-px" style={{ backgroundColor: "var(--border)" }} />
            </div>
          </div>
        )}

        {/* ── Form de LOGIN / esqueci senha ── */}
        {mode === "login" && (
          <form
            data-testid="login-form"
            onSubmit={e => {
              e.preventDefault();
              if (showForgotPassword) void submitForgotPassword();
              else if (loginStep === "email") void handleLoginContinue();
              else if (showPasswordLogin) void submit();
              else void handleLoginCodeVerify();
            }}
            className="flex flex-col gap-4"
          >
            {/* Email — etapa inicial e "esqueci minha senha" */}
            {(loginStep === "email" || showForgotPassword) && (
              <div className="flex flex-col gap-1.5">
                <label htmlFor="auth-login-email" style={{ ...sora, fontSize: 12, fontWeight: 400, color: "var(--foreground)" }}>{t("signup.email")}</label>
                <div className="relative">
                  <input
                    data-testid="login-email-input"
                    id="auth-login-email"
                    type="text"
                    required
                    autoComplete="username"
                    autoCapitalize="none"
                    value={email}
                    onChange={e => {
                      setEmail(e.target.value);
                      if (showForgotPassword) setForgotEmailValid(true);
                    }}
                    placeholder={t("signup.emailPlaceholder")}
                    style={{
                      ...inputBase,
                      ...(showForgotPassword && !forgotEmailValid ? { borderColor: "#ef4444" } : {}),
                    }}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                  />
                  <svg className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--muted-foreground)" }} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="20" height="16" x="2" y="4" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                  </svg>
                </div>
                {showForgotPassword && !forgotEmailValid && (
                  <span style={{ ...sora, fontSize: 11, color: "#ef4444" }}>{t("forgotPassword.invalidEmail")}</span>
                )}
              </div>
            )}

            {/* ── Etapa de CÓDIGO por email ── */}
            {!showForgotPassword && loginStep === "code" && !showPasswordLogin && (
              <div className="flex flex-col gap-1.5">
                <label htmlFor="auth-login-code" style={{ ...sora, fontSize: 12, fontWeight: 400, color: "var(--foreground)" }}>
                  {t("loginCode.codeLabel")}
                </label>
                <input
                  data-testid="login-code-input"
                  id="auth-login-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  autoFocus
                  value={loginCode}
                  onChange={e => setLoginCode(e.target.value.replace(/\D/g, ""))}
                  placeholder={t("loginCode.codePlaceholder")}
                  style={inputBase}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                />
                <p className="m-0" style={{ ...sora, fontSize: 12, fontWeight: 400, color: "var(--muted-foreground)" }}>
                  {t("loginCode.helper", { email: email.trim() })}
                </p>
              </div>
            )}

            {/* ── Modo SENHA (revelado pelo botão discreto) ── */}
            {!showForgotPassword && loginStep === "code" && showPasswordLogin && (
              <>
                {/* Senha */}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="auth-login-password" style={{ ...sora, fontSize: 12, fontWeight: 400, color: "var(--foreground)" }}>{t("signup.password")}</label>
                  <div className="relative">
                    <input
                      data-testid="login-password-input"
                      id="auth-login-password"
                      type={showPassword ? "text" : "password"}
                      required
                      autoComplete="current-password"
                      autoFocus
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder={t("signup.passwordPlaceholder")}
                      style={inputBase}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                    />
                    <button
                      data-testid="login-password-toggle"
                      type="button"
                      aria-label={showPassword ? t("acceptInvite.hidePassword") : t("acceptInvite.showPassword")}
                      onClick={() => setShowPassword(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 bg-transparent border-none cursor-pointer p-0"
                      style={{ color: "var(--muted-foreground)" }}
                    >
                      {showPassword ? <EyeOffSvg /> : <EyeOnSvg />}
                    </button>
                  </div>
                </div>

                {/* Lembrar / Esqueci */}
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer select-none" style={{ ...sora, fontSize: 12, fontWeight: 400, color: "var(--foreground)" }}>
                    <input data-testid="login-remember-checkbox" type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} className="w-4 h-4 rounded cursor-pointer" style={{ accentColor: "var(--primary)" }} />
                    {t("signup.rememberMe")}
                  </label>
                  <button data-testid="login-forgot-password" type="button" onClick={() => { setShowForgotPassword(true); setForgotEmailValid(true); }} className="border-none bg-transparent cursor-pointer hover:underline p-0" style={{ ...sora, fontSize: 14, fontWeight: 600, color: "var(--primary)" }}>
                    {t("signup.forgotPassword")}
                  </button>
                </div>
              </>
            )}

            {/* Submit */}
            <Button
              data-testid="login-submit"
              type="submit"
              className="w-full h-12 gap-4"
              style={jakartaSans}
              disabled={(showForgotPassword && forgotSending) || (!showForgotPassword && loginStep === "email" && loginCodeSending)}
              isLoading={(showForgotPassword && forgotSending) || (!showForgotPassword && loginStep === "email" && loginCodeSending)}
            >
              {showForgotPassword
                ? t("forgotPassword.submit")
                : loginStep === "email"
                  ? t("loginCode.continueSubmit")
                  : t("signup.loginSubmit")}
              <RocketSvg />
            </Button>

            {/* Etapa de código: reenviar + trocar email + botão discreto de senha */}
            {!showForgotPassword && loginStep === "code" && (
              <div className="flex flex-col gap-1 items-center">
                {!showPasswordLogin && (
                  <button
                    type="button"
                    onClick={resendLoginCode}
                    disabled={countdown > 0 || loginCodeSending}
                    className="border-none bg-transparent cursor-pointer hover:underline p-0"
                    style={{
                      ...sora,
                      fontSize: 14,
                      fontWeight: 600,
                      color: countdown > 0 || loginCodeSending ? "var(--muted-foreground)" : "var(--primary)",
                      height: 36,
                    }}
                  >
                    {countdown > 0 ? t("signup.resendCountdown", { countdown: String(countdown) }) : t("signup.resendCode")}
                  </button>
                )}

                <button
                  data-testid="login-change-email"
                  type="button"
                  onClick={() => {
                    setLoginStep("email");
                    setShowPasswordLogin(false);
                    setLoginCode("");
                    setPassword("");
                  }}
                  className="border-none bg-transparent cursor-pointer hover:underline p-0"
                  style={{ ...sora, fontSize: 14, fontWeight: 600, color: "var(--primary)", height: 36 }}
                >
                  {t("loginCode.changeEmail")}
                </button>

                {/* Botão discreto: alterna código ↔ senha */}
                <button
                  data-testid="login-toggle-password-mode"
                  type="button"
                  onClick={() => {
                    setShowPasswordLogin(v => !v);
                    if (!showPasswordLogin) setLoginCode("");
                    else lastAutoSubmittedLoginCodeRef.current = "";
                  }}
                  className="border-none bg-transparent cursor-pointer hover:underline p-0"
                  style={{ ...sora, fontSize: 12, fontWeight: 400, color: "var(--muted-foreground)", height: 32 }}
                >
                  {showPasswordLogin ? t("loginCode.useCode") : t("loginCode.usePassword")}
                </button>
              </div>
            )}

            {!showForgotPassword && loginStep === "email" && (
              <p className="text-center m-0" style={{ ...sora, fontSize: 16, fontWeight: 300, color: "var(--foreground)" }}>
                {t("signup.noAccount")}{" "}
                <button
                  data-testid="login-start-free"
                  type="button"
                  onClick={() => router.push("/signup")}
                  className="border-none bg-transparent cursor-pointer hover:underline p-0"
                  style={{ ...sora, fontSize: 16, fontWeight: 700, color: "var(--primary)" }}
                >
                  {t("signup.startFree")}
                </button>
              </p>
            )}

            {showForgotPassword && (
              <div className="flex justify-center w-full">
                <button
                  data-testid="login-forgot-back"
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotEmailValid(true);
                  }}
                  className="border-none bg-transparent cursor-pointer hover:underline p-0"
                  style={{ ...sora, fontSize: 14, fontWeight: 600, color: "var(--primary)" }}
                  disabled={forgotSending}
                >
                  {t("forgotPassword.back")}
                </button>
              </div>
            )}
          </form>
        )}

        {/* ── Form de CADASTRO (simplificado: só email) ── */}
        {mode === "signup" && signupStep === "form" && (
          <form data-testid="signup-email-form" onSubmit={e => { e.preventDefault(); handleSignup(); }} className="flex flex-col gap-4">
            {/* Email */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="auth-signup-email" style={{ ...sora, fontSize: 12, fontWeight: 400, color: "var(--foreground)" }}>{t("signup.email")}</label>
              <div className="relative">
                <input
                  data-testid="signup-email-input"
                  id="auth-signup-email"
                  type="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder={t("signup.emailPlaceholder")}
                  style={{
                    ...inputBase,
                    borderColor: signupTouched.has("email") && !emailValid ? "#ef4444" : undefined,
                  }}
                  onFocus={handleInputFocus}
                  onBlur={e => { handleInputBlur(e); touchSignupField("email"); }}
                />
                <svg className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--muted-foreground)" }} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect width="20" height="16" x="2" y="4" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                </svg>
              </div>
              {signupTouched.has("email") && !emailValid && (
                <span style={{ ...sora, fontSize: 11, color: "#ef4444" }}>{t("signup.emailInvalid")}</span>
              )}
            </div>

            {/* Terms */}
            <p className="text-center m-0" style={{ ...sora, fontSize: 8, fontWeight: 300, color: "#000" }}>
              {t("signup.termsText")}{" "}
              <a href="/termos" target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ ...sora, fontWeight: 700, color: "var(--primary)" }}>
                {t("signup.termsOfUse")}
              </a>
              {" "}{t("signup.termsAnd")}{" "}
              <a href="/termos" target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ ...sora, fontWeight: 700, color: "var(--primary)" }}>
                {t("signup.privacyPolicy")}
              </a>
              .
            </p>

            {/* Submit */}
            <Button data-testid="signup-submit" type="submit" className="w-full h-12 gap-4" disabled={!canSubmit} style={jakartaSans}>
              {t("signup.signupSubmit")}
              <RocketSvg />
            </Button>

            <p className="text-center m-0" style={{ ...sora, fontSize: 16, fontWeight: 300, color: "var(--muted-foreground)" }}>
              {t("signup.hasAccount")}{" "}
              <button
                type="button"
                onClick={() => router.push("/login")}
                className="border-none bg-transparent cursor-pointer hover:underline p-0"
                style={{ ...sora, fontSize: 16, fontWeight: 700, color: "var(--primary)" }}
              >
                {t("signup.loginLink")}
              </button>
            </p>

            {/* Dev bypass — localhost e HMG */}
            {isTestEnvironment && profile === "school" && (
              <button
                type="button"
                onClick={() => router.push("/signup/school-onboarding")}
                className="w-full border-none cursor-pointer hover:brightness-90 transition-all"
                style={{
                  ...sora,
                  height: 40,
                  borderRadius: 8,
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  backgroundColor: "#f0fdf4",
                  color: "#16a34a",
                  border: "1.5px dashed #86efac",
                }}
              >
                [DEV] Pular cadastro → school-onboarding
              </button>
            )}

          </form>
        )}

        {/* ── Step de verificação por e-mail ── */}
        {mode === "signup" && signupStep === "verify" && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="auth-verification-code" style={{ ...sora, fontSize: 12, fontWeight: 400, color: "var(--foreground)" }}>
                {t("signup.verificationCode")}
              </label>
              <input
                id="auth-verification-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                autoFocus
                value={verificationCode}
                onChange={e => setVerificationCode(e.target.value.replace(/\D/g, ""))}
                placeholder={t("signup.verificationPlaceholder")}
                style={inputBase}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
              <p className="m-0" style={{ ...sora, fontSize: 12, fontWeight: 400, color: "var(--muted-foreground)" }}>
                {t("signup.verificationHelper", { email })}
              </p>
            </div>

            <Button
              type="button"
              className="w-full h-12 gap-4"
              onClick={handleVerify}
              disabled={verificationCode.trim().length < 4}
            >
              {t("signup.verifySubmit")}
              <RocketSvg />
            </Button>

            <button
              type="button"
              onClick={resendCode}
              disabled={countdown > 0}
              className="w-full border-none bg-transparent cursor-pointer hover:underline"
              style={{
                ...sora,
                fontSize: 14,
                fontWeight: 600,
                color: countdown > 0 ? "var(--muted-foreground)" : "var(--primary)",
                height: 44,
              }}
            >
              {countdown > 0 ? t("signup.resendCountdown", { countdown: String(countdown) }) : t("signup.resendCode")}
            </button>
          </div>
        )}

      </div>
    </>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

interface AuthPageProps {
  initialMode: "login" | "signup";
  pageTitle: string;
}

export default function AuthPage({ initialMode, pageTitle }: AuthPageProps) {
  const { setLocale } = useI18n();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { currency: geoCurrency, detectedLocale, isLoading: geoLoading } = useGeoPricing();
  const geoLocaleApplied = useRef(false);

  // Cache do React Query é do usuário ANTERIOR até alguém limpar. O botão de
  // logout faz reload (window.location.href), o que recria o QueryClient — mas
  // expiração de sessão só faz router.push("/login"), navegação SPA que preserva
  // o cache inteiro. Sem isto, o próximo login na mesma aba (computador
  // compartilhado de escola) lia dados da conta anterior: em particular
  // "user-info-onboarding", cuja leitura stale trava o ref de evidência
  // pegajosa do OnboardingGate com o valor errado. Chegar na tela de login é o
  // ponto natural para descartar tudo — nada aqui depende do cache.
  useEffect(() => {
    queryClient.clear();
  }, [queryClient]);

  useEffect(() => {
    if (!geoLoading && detectedLocale && !geoLocaleApplied.current) {
      geoLocaleApplied.current = true;
      if (!readStoredLocale()) {
        setLocale(detectedLocale);
      }
    }
  }, [geoLoading, detectedLocale, setLocale]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const e = params.get("email");
    const p = params.get("password");
    if (e && p) {
      clearLocalStoragePreservingLocale();
      AuthService.logout();
      return;
    }

    async function checkIsLogged() {
      try {
        if (AuthService.isLogged()) {
          const userSettings = Settings.getSettings();
          const currentAccount = userSettings?.accountsDetails?.[0];
          const mustFinishSignup = Boolean(currentAccount?.["new-manager-global"] && !currentAccount?.signupPlanCompletedAt);
          if (mustFinishSignup) {
            if (currentAccount?.signupProfile === "teacher") {
              try {
                // B2C Free Flow: entra no Free e vai ao onboarding (A/B).
                await loginClient.completeSignupFreePlan("teacher");
                Settings.unloadSettings();
                await Settings.loadSettings();
                // Mesma regra do redirectAfterLogin: só manda pro wizard com
                // evidência POSITIVA de onboarding pendente. Este caminho
                // (usuário já logado abrindo /login ou /signup) decidia apenas
                // por `signupPlanCompletedAt` e reenviava ao wizard quem já
                // tinha concluído (2026-07-29).
                const completedLocal = hasLocalOnboardingCompletion();
                const { completed, checkFailed } = await readRemoteOnboardingCompletion(loginClient);
                router.push(
                  completedLocal || completed || checkFailed
                    ? "/chat-ia"
                    : await resolveOnboardingRedirectTarget(),
                );
                return;
              } catch (err: any) {
                console.error("[completeSignupFreePlan] login checkIsLogged failed:", err?.message || err?.error || err);
                router.push("/signup/plans");
                return;
              }
            }
            router.push(getSchoolSignupContinuation(currentAccount, userSettings?.setup_completed));
            return;
          }
          const groups = currentAccount?.groups || [];
          const isNewManagerGlobal = Boolean(currentAccount?.["new-manager-global"]);
          if (isNewManagerGlobal) {
            router.push("/chat-ia");
          } else if (groups.includes("admin") || groups.includes("financial_admin")) {
            router.push("/dashboard");
          } else {
            router.push("/details");
          }
        }
      } catch (error) {
        console.error(error);
      }
    }

    checkIsLogged();
  }, []);

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>

      <div className="gds flex flex-col w-full h-screen">
        <GamifikTopBar />

        <div className="flex flex-1 min-h-0 flex-col md:flex-row overflow-y-auto md:overflow-hidden">
          {/* ── Right: carousel — hidden on mobile ── */}
          <div className="hidden md:block order-1 md:order-2 shrink-0 md:shrink md:flex-1 bg-white">
            <LoginCarousel />
          </div>

          {/* ── Left: form ── */}
          <div
            className="order-2 md:order-1 flex flex-1 justify-center md:overflow-y-auto"
            style={{ background: "var(--secondary)", scrollbarWidth: "none" }}
          >
            <div className="w-full px-6 mb-16 md:mb-0" style={{ maxWidth: 492 }}>
              <LoginForm initialMode={initialMode} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
